import type { PdfDocument } from '../converter/pdf-loader'
import { cleanupCanvas } from '../utils/memory'

const THUMBNAIL_TARGET_WIDTH = 160
const DRAG_THRESHOLD = 6 // px of movement before drag starts

/**
 * Creates an interactive page selector with thumbnails.
 *
 * - Renders a grid of page thumbnails (lazy-loaded via IntersectionObserver)
 * - Click a thumbnail to toggle selection
 * - Drag thumbnails to reorder
 * - Selected pages are highlighted with an order badge
 * - Reports the ordered selection via onChange
 */
export function createPageSelector(
  container: HTMLElement,
  pdf: PdfDocument,
  numPages: number,
  onChange: (selectedPages: number[]) => void,
): { getSelectedPages: () => number[] } {
  // pageOrder holds all page numbers in their current visual order.
  // selectedSet holds the set of selected page numbers.
  let pageOrder: number[] = Array.from({ length: numPages }, (_, i) => i + 1)
  let selectedSet = new Set<number>()
  const thumbnailCache = new Map<number, string>()
  const renderQueue = new Set<number>()
  let rendering = false

  container.innerHTML = `
    <div class="page-selector">
      <div class="page-selector__toolbar">
        <span class="page-selector__count" id="pageCount">0 selected</span>
        <div class="page-selector__actions">
          <button type="button" class="page-selector__btn" id="selectAllBtn">Select all</button>
          <button type="button" class="page-selector__btn" id="clearBtn">Clear</button>
        </div>
      </div>
      <p class="page-selector__hint">Click thumbnails to select. Drag to reorder.</p>
      <div class="page-selector__grid" id="thumbnailGrid" role="list"></div>
    </div>
  `

  const grid = container.querySelector<HTMLDivElement>('#thumbnailGrid')!
  const countEl = container.querySelector<HTMLSpanElement>('#pageCount')!

  // Build grid cards in initial order
  for (const page of pageOrder) {
    grid.appendChild(createCard(page))
  }

  // Lazy-load thumbnails via IntersectionObserver
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const page = parseInt(
            (entry.target as HTMLElement).dataset.page!,
            10,
          )
          queueThumbnailRender(page)
          observer.unobserve(entry.target)
        }
      }
    },
    { root: grid, rootMargin: '200px' },
  )

  grid.querySelectorAll('.thumb-card').forEach((card) => observer.observe(card))

  // ─── Thumbnail rendering ───────────────────────────────────

  function queueThumbnailRender(page: number): void {
    if (thumbnailCache.has(page)) {
      applyThumbnail(page)
      return
    }
    renderQueue.add(page)
    void processRenderQueue()
  }

  async function processRenderQueue(): Promise<void> {
    if (rendering) return
    rendering = true
    while (renderQueue.size > 0) {
      const page = renderQueue.values().next().value!
      renderQueue.delete(page)
      if (thumbnailCache.has(page)) {
        applyThumbnail(page)
        continue
      }
      try {
        const dataUrl = await renderThumbnail(pdf, page, THUMBNAIL_TARGET_WIDTH)
        thumbnailCache.set(page, dataUrl)
        applyThumbnail(page)
      } catch (err) {
        console.error(`Thumbnail render failed for page ${page}:`, err)
      }
      await new Promise((r) => setTimeout(r, 0))
    }
    rendering = false
  }

  function applyThumbnail(page: number): void {
    const dataUrl = thumbnailCache.get(page)
    if (!dataUrl) return
    const card = grid.querySelector<HTMLElement>(
      `.thumb-card[data-page="${page}"]`,
    )
    if (!card) return
    const placeholder = card.querySelector('.thumb-card__placeholder')
    if (placeholder) {
      const img = document.createElement('img')
      img.src = dataUrl
      img.alt = `Page ${page}`
      img.className = 'thumb-card__img'
      placeholder.replaceWith(img)
    }
  }

  // ─── Card creation ─────────────────────────────────────────

  function createCard(page: number): HTMLElement {
    const card = document.createElement('div')
    card.className = 'thumb-card'
    card.dataset.page = String(page)
    card.setAttribute('role', 'listitem')
    card.setAttribute('aria-label', `Page ${page}`)
    card.tabIndex = 0
    card.innerHTML = `
      <div class="thumb-card__checkbox" aria-hidden="true">
        <svg class="thumb-card__check" viewBox="0 0 16 16" width="14" height="14">
          <path d="M3 8l3 3 7-7" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <span class="thumb-card__order" aria-hidden="true"></span>
      <div class="thumb-card__drag-handle" aria-label="Drag to reorder" role="button" tabindex="-1">
        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
          <circle cx="5" cy="4" r="1.3" fill="currentColor"/><circle cx="11" cy="4" r="1.3" fill="currentColor"/>
          <circle cx="5" cy="8" r="1.3" fill="currentColor"/><circle cx="11" cy="8" r="1.3" fill="currentColor"/>
          <circle cx="5" cy="12" r="1.3" fill="currentColor"/><circle cx="11" cy="12" r="1.3" fill="currentColor"/>
        </svg>
      </div>
      <div class="thumb-card__img-wrap">
        <div class="thumb-card__placeholder">Page ${page}</div>
      </div>
      <span class="thumb-card__label">${page}</span>
    `
    setupCardInteractions(card)
    return card
  }

  // ─── Selection ─────────────────────────────────────────────

  function togglePage(page: number): void {
    if (selectedSet.has(page)) {
      selectedSet.delete(page)
    } else {
      selectedSet.add(page)
    }
    updateUI()
    notifyChange()
  }

  function selectAll(): void {
    selectedSet = new Set(pageOrder)
    updateUI()
    notifyChange()
  }

  function clearSelection(): void {
    selectedSet.clear()
    updateUI()
    notifyChange()
  }

  function updateUI(): void {
    const count = selectedSet.size
    countEl.textContent = `${count} ${count === 1 ? 'page' : 'pages'} selected`

    // Update each card's selected state and order badge
    let orderIdx = 0
    for (const page of pageOrder) {
      const card = grid.querySelector<HTMLElement>(
        `.thumb-card[data-page="${page}"]`,
      )
      if (!card) continue
      const isSelected = selectedSet.has(page)
      card.classList.toggle('thumb-card--selected', isSelected)
      card.setAttribute('aria-selected', String(isSelected))

      const badge = card.querySelector<HTMLSpanElement>('.thumb-card__order')!
      if (isSelected) {
        orderIdx++
        badge.textContent = String(orderIdx)
        badge.style.display = ''
      } else {
        badge.style.display = 'none'
      }
    }
  }

  function notifyChange(): void {
    onChange(getSelectedPages())
  }

  function getSelectedPages(): number[] {
    return pageOrder.filter((p) => selectedSet.has(p))
  }

  // ─── Drag-to-reorder ───────────────────────────────────────

  function setupCardInteractions(card: HTMLElement): void {
    const page = parseInt(card.dataset.page!, 10)
    const dragHandle = card.querySelector<HTMLElement>('.thumb-card__drag-handle')!
    let pointerStart: { x: number; y: number } | null = null
    let isDragging = false

    // Click on card body toggles selection
    card.addEventListener('click', (e) => {
      // Ignore clicks that originated from the drag handle
      if (e.target instanceof Node && dragHandle.contains(e.target)) return
      togglePage(page)
    })

    // Drag starts only from the drag handle
    dragHandle.addEventListener('pointerdown', (e) => {
      e.stopPropagation()
      pointerStart = { x: e.clientX, y: e.clientY }
      dragHandle.setPointerCapture(e.pointerId)
    })

    dragHandle.addEventListener('pointermove', (e) => {
      if (!pointerStart) return
      const dx = e.clientX - pointerStart.x
      const dy = e.clientY - pointerStart.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (!isDragging && dist > DRAG_THRESHOLD) {
        isDragging = true
        card.classList.add('thumb-card--dragging')
      }

      if (isDragging) {
        e.preventDefault()
        // Find the card under the pointer (excluding the dragged card)
        card.style.pointerEvents = 'none'
        const el = document.elementFromPoint(e.clientX, e.clientY)
        card.style.pointerEvents = ''
        const target = el?.closest('.thumb-card') as HTMLElement | null
        if (target && target !== card && target.parentElement === grid) {
          swapCards(card, target)
        }
      }
    })

    const endDrag = (): void => {
      if (isDragging) {
        card.classList.remove('thumb-card--dragging')
        isDragging = false
        notifyChange()
      }
      pointerStart = null
    }

    dragHandle.addEventListener('pointerup', endDrag)
    dragHandle.addEventListener('pointercancel', endDrag)

    // Keyboard: Enter/Space toggles selection
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        togglePage(page)
      }
    })
  }

  function swapCards(a: HTMLElement, b: HTMLElement): void {
    const pageA = parseInt(a.dataset.page!, 10)
    const pageB = parseInt(b.dataset.page!, 10)
    const idxA = pageOrder.indexOf(pageA)
    const idxB = pageOrder.indexOf(pageB)
    if (idxA === -1 || idxB === -1) return

    // Swap in data model
    pageOrder[idxA] = pageB
    pageOrder[idxB] = pageA

    // Swap in DOM
    const aNext = a.nextSibling
    const bNext = b.nextSibling
    if (aNext === b) {
      grid.insertBefore(b, a)
    } else if (bNext === a) {
      grid.insertBefore(a, b)
    } else {
      grid.insertBefore(b, aNext)
      grid.insertBefore(a, bNext)
    }

    updateUI()
  }

  // ─── Toolbar buttons ───────────────────────────────────────

  container
    .querySelector<HTMLButtonElement>('#selectAllBtn')!
    .addEventListener('click', selectAll)

  container
    .querySelector<HTMLButtonElement>('#clearBtn')!
    .addEventListener('click', clearSelection)

  return { getSelectedPages }
}

/**
 * Renders a single PDF page to a JPEG data URL at a small scale
 * suitable for thumbnail display. Cleans up the canvas afterward.
 */
async function renderThumbnail(
  pdf: PdfDocument,
  pageNumber: number,
  targetWidth: number,
): Promise<string> {
  const page = await pdf.getPage(pageNumber)
  const baseViewport = page.getViewport({ scale: 1 })
  const scale = Math.min(targetWidth / baseViewport.width, 0.5)
  const viewport = page.getViewport({ scale })

  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(viewport.width)
  canvas.height = Math.ceil(viewport.height)

  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context not available')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  await page.render({ canvasContext: ctx, viewport }).promise
  page.cleanup()

  const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
  cleanupCanvas(canvas)

  return dataUrl
}
